import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { Col, Collapse, Row, Skeleton } from "antd";
import {
  ChevronUp,
  ChevronDown,
  PhoneCall,
  Mail,
  Handshake,
  Monitor,
  MessageSquare,
} from "lucide-react";

import {
  getGroupedByActivityData,
  getGroupedByActivityStatus,
  getGroupedByCallActivityData,
  getGroupedByMeetingActivityData,
  getGroupedByEmailActivityData,
} from "../../actionCreator/dashboardActionCreator";
import { getUserInfo } from "../../utils/utils";
import ChartRenderer from "./ChartRenderer";
import seprator from "../../assets/svg/icons/seprator.svg";

const SERIES_COLOR_MAP = {
  call: "#2F80ED",
  email: "#27AE60",
  meeting: "#9B51E0",
  chat: "#F2994A",
  whatsapp: "#16A34A",
  demo: "#EB5757",
  followup: "#14B8A6",
  "follow-up": "#14B8A6",
  completed: "#2563EB",
  open: "#F59E0B",
  cancelled: "#EF4444",
  "re-scheduled": "#8B5CF6",
  others: "#94A3B8",
};

const normalizeActivityType = (value) => {
  const normalizedValue = String(value || "").trim();

  if (
    !normalizedValue ||
    normalizedValue.toLowerCase() === "null" ||
    normalizedValue.toLowerCase() === "undefined"
  ) {
    return "Others";
  }

  return normalizedValue;
};

const getSeriesColor = (value) => {
  const normalizedValue = normalizeActivityType(value).toLowerCase();
  return SERIES_COLOR_MAP[normalizedValue] || "#64748B";
};

const getFlattenedActivityRecords = (groupedActivityData) => {
  if (!groupedActivityData || typeof groupedActivityData !== "object") {
    return [];
  }

  const monthKeys = Object.keys(groupedActivityData).filter((key) =>
    /^\d{4}-\d{2}$/.test(key),
  );

  if (monthKeys.length > 0) {
    return monthKeys
      .sort()
      .flatMap((monthKey) =>
        (groupedActivityData?.[monthKey]?.dashboardDTOS || []).map((record) => ({
          ...record,
          statusId: record?.statusId || monthKey,
        })),
      );
  }

  if (Array.isArray(groupedActivityData?.dashboardDTOS)) {
    return groupedActivityData.dashboardDTOS;
  }

  return [];
};

const getFlattenedDashboardDtos = (dashboardData) => {
  if (!dashboardData || typeof dashboardData !== "object") {
    return [];
  }

  if (Array.isArray(dashboardData?.dashboardDTOS)) {
    return dashboardData.dashboardDTOS;
  }

  const monthKeys = Object.keys(dashboardData).filter((key) =>
    /^\d{4}-\d{2}$/.test(key),
  );

  if (!monthKeys.length) {
    return [];
  }

  return monthKeys
    .sort()
    .flatMap((monthKey) =>
      (dashboardData?.[monthKey]?.dashboardDTOS || []).map((record) => ({
        ...record,
        statusId: record?.statusId || monthKey,
      })),
    );
};

const ActivitiesReport = ({ userId }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const requestRef = useRef(0);
  const groupedActivityData = useSelector(
    (state) => state.DashboardReducer.groupedActivityData,
  );
  const groupedActivityStatusData = useSelector(
    (state) => state.DashboardReducer.groupedActivityStatusData,
  );

  const groupedActivityCallData = useSelector(
    (state) => state.DashboardReducer.groupedActivityCallData,
  );
  const groupedActivityMeetingData = useSelector(
    (state) => state.DashboardReducer.groupedActivityMeetingData,
  );
  const groupedActivityEmailData = useSelector(
    (state) => state.DashboardReducer.groupedActivityEmailData,
  );

  useEffect(() => {
    if (Object.keys(getUserInfo()).length) {
      const payload = { userId };
      const requestId = ++requestRef.current;
      setIsLoading(true);
      Promise.all([
        dispatch(getGroupedByActivityData(payload)),
        dispatch(getGroupedByActivityStatus(payload)),
        dispatch(getGroupedByCallActivityData(payload)),
        dispatch(getGroupedByMeetingActivityData(payload)),
        dispatch(getGroupedByEmailActivityData(payload)),
      ]).finally(() => {
        if (requestId === requestRef.current) {
          setIsLoading(false);
        }
      });
    }
  }, [dispatch, userId]);
  const flattenedActivityRecords = getFlattenedActivityRecords(groupedActivityData);
  const flattenedActivityStatusRecords =
    getFlattenedDashboardDtos(groupedActivityStatusData);
  const activityByStatusdata = _.orderBy(
    flattenedActivityStatusRecords.map((record) => ({
      ...record,
      statusId: String(record?.statusId || ""),
      countSum: parseInt(record?.countSum, 10) || 0,
      typeValue: normalizeActivityType(record?.typeValue),
      order: normalizeActivityType(record?.typeValue) === "Others" ? "b" : "a",
    })),
    ["statusId", "order", "typeValue"],
    ["asc", "asc", "asc"],
  );
  let leadByStatusdata = [];
  let leadByStatusdataOrg = [];
  if (flattenedActivityRecords?.length > 0) {
    flattenedActivityRecords?.forEach((record) => {
      leadByStatusdataOrg.push({
        ...record,
        statusId: String(record?.statusId || ""),
        typeValue: normalizeActivityType(record?.typeValue),
        order: normalizeActivityType(record?.typeValue) === "Others" ? "b" : "a",
      });
    });
    leadByStatusdata = _.orderBy(leadByStatusdataOrg, "order", "asc");
  }
  const groupedActivityCallDataArr =
    getFlattenedDashboardDtos(groupedActivityCallData);
  const groupedActivityMeetingDataArr =
    getFlattenedDashboardDtos(groupedActivityMeetingData);
  const groupedActivityEmailDataArr =
    getFlattenedDashboardDtos(groupedActivityEmailData);

  const typeValueBlock = [
    "meeting",
    "call",
    "email",
    "chat",
    "whatsapp",
    "demo",
  ];

  const visibleActivities =
    leadByStatusdata?.filter((record) =>
      typeValueBlock.includes(record?.typeValue?.toLowerCase?.()),
    ) || [];

  const activitySummary = Object.values(
    leadByStatusdata.reduce((acc, record) => {
      const typeValue = normalizeActivityType(record?.typeValue);
      const key = typeValue.toLowerCase();

      if (!acc[key]) {
        acc[key] = {
          typeValue,
          countSum: 0,
        };
      }

      acc[key].countSum += parseInt(record?.countSum, 10) || 0;
      return acc;
    }, {}),
  );

  const visibleActivitySummary = activitySummary.filter((record) =>
    typeValueBlock.includes(record?.typeValue?.toLowerCase?.()),
  );


  const activityTrendData = leadByStatusdata.map((record) => ({
    label: String(record?.statusId || ""),
    value: parseInt(record?.countSum, 10) || 0,
    category: normalizeActivityType(record?.typeValue),
  }));

  let otherValues = 0;
  const getActivityIcon = (typeValue) => {
    const type = typeValue?.toLowerCase();
    if (type === "call") return <PhoneCall className="tw-w-4 tw-h-4" />;
    if (type === "email") return <Mail className="tw-w-4 tw-h-4" />;
    if (type === "meeting") return <Handshake className="tw-w-4 tw-h-4" />;
    if (type === "demo") return <Monitor className="tw-w-4 tw-h-4" />;
    return <MessageSquare className="tw-w-4 tw-h-4" />;
  };

  return (
    <div className="tw-bg-[#f8fcff] tw-p-5">
      <div className="tw-mb-6">
        <Collapse
          expandIcon={({ isActive }) =>
            isActive ? <ChevronUp className="tw-w-4 tw-h-4" /> : <ChevronDown className="tw-w-4 tw-h-4" />
          }
          className="tw-advanced-filter"
          expandIconPosition="end"
          items={[
            {
              label: (
                <div className="tw-text-base tw-font-semibold">Activities</div>
              ),
              children: (
                <Skeleton active loading={isLoading} paragraph={{ rows: 2 }}>
                  <Row className="tw-px-4 tw-py-2">
                    {activitySummary?.forEach((record) => {
                      const { typeValue } = record || {};
                      if (!typeValueBlock.includes(typeValue?.toLowerCase())) {
                        otherValues +=
                          record.countSum && parseInt(record.countSum, 10);
                      }
                    })}

                    {visibleActivitySummary?.map((record, i) => {
                      const { typeValue, countSum } = record || {};
                      return (
                        <React.Fragment key={typeValue}>
                          {i > 0 && (
                            <Col md={1}>
                              <img src={seprator} alt="" />
                            </Col>
                          )}
                          <Col md={3}>
                            <div className="tw-flex tw-flex-col tw-gap-1 tw-pb-1">
                              <div
                                className="tw-flex tw-items-center tw-justify-center tw-w-[34px] tw-h-[34px] tw-rounded-full"
                                style={{
                                  backgroundColor: `${getSeriesColor(
                                    typeValue,
                                  )}1A`,
                                  color: getSeriesColor(typeValue),
                                }}
                              >
                                {getActivityIcon(typeValue)}
                              </div>
                              <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                                {countSum}
                              </div>
                              <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                                {typeValue}
                              </div>
                            </div>
                          </Col>
                        </React.Fragment>
                      );
                    })}

                    {visibleActivities.length > 0 && (
                      <Col md={1}>
                        <img src={seprator} alt="" />
                      </Col>
                    )}

                    <Col md={3}>
                      <div className="tw-flex tw-flex-col tw-gap-1 tw-pb-1">
                        <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                          <i
                            className={`las la-hand-pointer tw-text-text-primary heading20px`}
                          ></i>
                        </div>
                        <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                          {otherValues}
                        </div>
                        <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                          Others
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Skeleton>
              ),
            },
          ]}
        />
      </div>


      <Row gutter={[24, 24]} className="mx-0 mb20">
        <Col xs={24} md={8} className="!tw-pl-0">
          <ChartRenderer
            loading={isLoading}
            title="Activity By Type"
            type="column"
            data={activityTrendData}
            customConfig={{
              isStack: true,
              xField: "label",
              yField: "value",
              seriesField: "category",
              color: (datum) => getSeriesColor(datum?.category || datum?.label || (typeof datum === "string" ? datum : "")),
              xAxis: {
                title: {
                  text: "Year / Month",
                },
              },
              yAxis: {
                title: {
                  text: "Count",
                },
              },
              legend: {
                position: "bottom",
              },
              tooltip: {
                formatter: (datum) => ({
                  name: datum.category,
                  value: datum.value,
                }),
              },
            }}
          />
        </Col>
        <Col xs={24} md={8}>
          <ChartRenderer
            loading={isLoading}
            title="Activity By Status"
            type="column"
            data={activityByStatusdata?.map((d) => ({
              label: d?.statusId || "",
              value: d?.countSum || 0,
              category: d?.typeValue || "",
            }))}
            customConfig={{
              isStack: true,
              xField: "label",
              yField: "value",
              seriesField: "category",
              color: (datum) => getSeriesColor(datum?.category || datum?.label || (typeof datum === "string" ? datum : "")),
              xAxis: {
                title: {
                  text: "Year / Month",
                },
              },
              yAxis: {
                title: {
                  text: "Count",
                },
              },
              legend: {
                position: "bottom",
              },
              tooltip: {
                formatter: (datum) => ({
                  name: datum.category,
                  value: datum.value,
                }),
              },
            }}
          />
        </Col>
        <Col xs={24} md={8} className="!tw-pr-0">
          <ChartRenderer
            loading={isLoading}
            title="Most Meeting Held (Top 5)"
            type="column"
            data={groupedActivityMeetingDataArr?.map((d) => ({
              value: parseFloat(d?.countSum) || 0,
              label: d?.statusId || "",
            }))}
          />
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mx-0 mb20">
        <Col xs={24} md={8} className="!tw-pl-0">
          <ChartRenderer
            loading={isLoading}
            title="Most Email Sent (Top 5)"
            type="column"
            data={groupedActivityEmailDataArr?.map((d) => ({
              value: parseFloat(d?.countSum) || 0,
              label: d?.statusId || "",
            }))}
          />
        </Col>
        <Col xs={24} md={8} className="!tw-pr-0">
          <ChartRenderer
            loading={isLoading}
            title="Most Call Made (Top 5)"
            type="column"
            data={groupedActivityCallDataArr?.map((d) => ({
              value: parseFloat(d?.countSum) || 0,
              label: d?.statusId || "",
            }))}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ActivitiesReport;
